<template>
    <component
        :is="isSelectable ? 'button' : 'div'"
        :type="isSelectable ? 'button' : null"
        class="reward-card"
        :class="{
            'reward-card--unlocked': isUnlocked,
            'reward-card--muted': isMuted,
            'reward-card--selectable': isSelectable,
            'reward-card--plain': plain,
        }"
        @click="isSelectable ? $emit('select', reward) : null"
    >
        <div class="reward-card-image" v-if="hasImage">
            <img :src="reward.image.url" :alt="reward.name" loading="lazy" />
        </div>

        <div class="reward-card-body" v-if="!isBlurred">
            <div class="reward-card-tags">
                <span class="reward-card-tag" :class="'reward-card-tag--' + tagVariant">
                    <svg v-if="isLocked" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    {{ tagLabel }}
                </span>
            </div>

            <div class="reward-card-text">
                <h3 class="reward-card-title">{{ reward.name }}</h3>
                <p class="reward-card-description" v-if="description">{{ description }}</p>
            </div>
        </div>

        <div class="reward-card-skeleton" v-else aria-hidden="true">
            <span class="reward-card-skeleton-image"></span>
            <span class="reward-card-skeleton-lines">
                <span></span>
                <span></span>
                <span></span>
            </span>
        </div>

        <span class="reward-card-medal" v-if="memberTier" :title="memberTier.name">
            <tier-icon :tier="memberTier" />
        </span>

        <div class="reward-card-veil" v-if="isBlurred">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#4d5761" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span v-lang="'dashboard-rewardExclusive'"></span>
            <span
                class="reward-card-tier-chip"
                v-if="lockTier"
                :style="{ background: lockTier.color, color: lockTier.textColor }"
            >
                <tier-icon :tier="lockTier" />
                {{ lockTier.name }}
            </span>
        </div>
    </component>
</template>

<script>
import TierIcon from "../../shared/tier-icon";

export default {
    name: "RewardCard",
    components: {
        TierIcon,
    },
    props: {
        reward: {
            type: Object,
            required: true,
        },
        balance: {
            type: Number,
            default: 0,
        },
        isLogged: {
            type: Boolean,
            default: false,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        plain: {
            type: Boolean,
            default: false,
        },
        tiers: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        hasImage() {
            return !!(this.reward.image && this.reward.image.url);
        },
        description() {
            return this.reward.subtitle || this.reward.description || "";
        },
        access() {
            return this.reward.access || "available";
        },
        isBlurred() {
            return this.access === "blurred";
        },
        isLocked() {
            return this.access === "locked";
        },
        rewardTiers() {
            const ids = this.reward.tierIds || [];

            return this.tiers
                .filter((tier) => ids.includes(tier.id))
                .sort((a, b) => a.position - b.position);
        },
        lockTier() {
            return this.isLocked || this.isBlurred ? this.rewardTiers[0] || null : null;
        },
        memberTier() {
            if (this.access !== "available") {
                return null;
            }

            return this.rewardTiers.find((tier) => tier.isMember) || null;
        },
        isUnlocked() {
            if (this.plain) {
                return true;
            }

            return this.isLogged && this.access === "available" && this.balance >= this.reward.points;
        },
        isMuted() {
            return !this.isUnlocked;
        },
        isSelectable() {
            return this.selectable && !this.isBlurred;
        },
        tagVariant() {
            return this.isUnlocked ? "unlocked" : "locked";
        },
        tagLabel() {
            if (this.isLocked && this.lockTier) {
                return this.lockTier.name;
            }

            return this.t("dashboard-rewardPoints", { points: this.reward.points });
        },
    },
};
</script>

<style lang="scss">
.reward-card {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 1rem;
  background: #fcfcfd;
  border: 1px dashed #9da4ae;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.1s;
  text-align: left;
  font: inherit;
}

.reward-card--selectable {
  cursor: pointer;

  &:active {
    transform: scale(0.99);
  }
}

.reward-card--unlocked {
  border-style: solid;
  border-color: #17b26a;
  box-shadow:
    0 0.3125rem 0.75rem rgba(0, 0, 0, 0.07),
    0 1.375rem 1.375rem rgba(0, 0, 0, 0.06),
    0 3.0625rem 1.8125rem rgba(0, 0, 0, 0.04);
}

.reward-card--plain {
  border-color: transparent;
  box-shadow: none;
}

.reward-card--muted .reward-card-image img {
  filter: grayscale(1);
  opacity: 0.65;
}

.reward-card--muted .reward-card-title,
.reward-card--muted .reward-card-description {
  color: #6c737f;
}

.reward-card-image {
  flex: 0 0 auto;
  width: clamp(5rem, 31vw, 7.3125rem);
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;

  @supports not (aspect-ratio: 1) {
    height: clamp(5rem, 31vw, 7.3125rem);
  }
}

.reward-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.reward-card-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
}

.reward-card-tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.reward-card-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.3;
  white-space: nowrap;
}

.reward-card-tag--locked {
  background: #e5e7eb;
  color: #4d5761;
}

.reward-card-tag--unlocked {
  background: #d3f8e0;
  color: #0b9055;
}

.reward-card-text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.reward-card-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: #4d5761;
  overflow-wrap: anywhere;
}

.reward-card-description {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: #4d5761;
  overflow-wrap: anywhere;
}

.reward-card-tag svg {
  flex: 0 0 auto;
  margin-right: 0.25rem;
}

.reward-card-medal {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  font-size: 1.25rem;
  line-height: 1;
}

.reward-card-skeleton {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 5rem;
  filter: blur(0.25rem);
}

.reward-card-skeleton-image {
  flex: 0 0 auto;
  width: clamp(5rem, 31vw, 7.3125rem);
  aspect-ratio: 1;
  border-radius: 0.5rem;
  background: #e5e7eb;
}

.reward-card-skeleton-lines {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  span {
    display: block;
    height: 0.75rem;
    border-radius: 0.375rem;
    background: #e5e7eb;
  }

  span:first-child {
    width: 30%;
  }

  span:nth-child(2) {
    width: 70%;
  }

  span:last-child {
    width: 50%;
  }
}

.reward-card-tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
}

.reward-card-veil {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  bottom: 0.5rem;
  left: 0.5rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.56);
  backdrop-filter: blur(0.25rem);
  -webkit-backdrop-filter: blur(0.25rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.3;
  color: #4d5761;
}
</style>
